import React, { useEffect, useRef, useMemo } from "react";
import _ from 'lodash';
import { Link, useLocation } from 'wouter';
import { useDispatch, useSelector } from 'react-redux';
import PDFSVG from "~/components/PDFSVG.jsx";
import { useDataEnv } from "~/functions/DataEnv.js";
import { canAccessFeature, paidPlan } from "~/functions/Access.js";
import useCurrentAccount from '~/hooks/useCurrentAccount.js';
import { actions } from '@/store/store.js';
import redirect from '@/functions/redirect.js';
import DottedSpinner from "~/components/DottedSpinner.jsx";
import Button from "~/components/Button.jsx";

const MiracleFundamentalsLanding = ({ pods, podule }) => {
    const dataEnv = useDataEnv();
    const featurePlan = _.get(dataEnv, 'current_account.feature_plan');
    const hasRenderedOnce = useRef(false);
    const [, navigate] = useLocation();
    const hasAccess = canAccessFeature(featurePlan, podule);
    const hasPaidPlan = paidPlan(featurePlan);

    const dispatch = useDispatch();
    const allCertificationProficiencies = useSelector(({ certificationProficiencies }) => certificationProficiencies?.records);
    const certificationProficiencies = useMemo(() =>
            Object.values(allCertificationProficiencies)
                ?.filter((cp) => cp.certification_id === podule.id)
                ?.sort((a, b) => a.sequence > b.sequence ? 1 : -1),
        [allCertificationProficiencies]
    );
    const proficiencies = useSelector((state) => (
        Object.values(state.proficiencies?.records)?.filter((prof) => certificationProficiencies.some((certProf) => (
            certProf.proficiency_id === prof.id
        )))
    ));
    const proficienciesLoading = useSelector(({ proficiencies }) => proficiencies.loading);
    const proficiencyCompletions = useSelector((state) => {
        const certificationProficiencyIds = certificationProficiencies?.map((p) => p.id);
        return Object.values(state.proficiencyCompletions?.records)
            ?.filter(completion => certificationProficiencyIds.includes(completion.certification_proficiency_id))
    });

    const { currentEnrollmentsLoading } = useSelector((state) => state.enrollments);
    const thisEnrollment = useSelector(
        ({ enrollments }) =>
            Object.values(enrollments?.currentEnrollments).find((e) => e.certification_id === podule.id)
    );

    const trainingProfile = useSelector(({ trainingProfiles }) => trainingProfiles.currentTrainingProfile);

    const examResults = useSelector(({ certificationExamResults }) => Object.values(certificationExamResults.records).filter((examResult) => {
        return examResult.training_profile_id === trainingProfile?.id && examResult.certification_id === podule.id;
    }));
    const passingResult = useMemo(() => examResults?.find(result => result.pass), [examResults]);

    useEffect(() => {
        dispatch(actions.certificationProficiencies.loadByCertificationId(podule.id))
        dispatch(actions.enrollments.loadCurrentEnrollments());
        dispatch(actions.trainingProfiles.loadCurrentTrainingProfile());
        dispatch(actions.certificationExamResults.query({ certification_id: podule.id }));
    }, []);

    useEffect(() => {
        const proficiencyIds = certificationProficiencies?.map((cp) => cp.proficiency_id);
        if (proficiencyIds) dispatch(actions.proficiencies.loadByIds(proficiencyIds));
    }, [certificationProficiencies]);

    useEffect(() => {
        dispatch(actions.proficiencyCompletions.loadByCertificationId(podule.id));
    }, []);

    const { currentAccountLoading, currentAccount } = useCurrentAccount();

    useEffect(() => {
        if (!hasAccess || (!currentAccount && !currentAccountLoading && hasRenderedOnce.current)) {
            setTimeout(() => {
                redirect(Routes.path_path('pricing'));
            }, 2000);
        }
        hasRenderedOnce.current = true;
    }, [currentAccount, currentAccountLoading]);

    const createEnrollment = () => {
        dispatch(actions.enrollments.create({ certification_id: podule.id }));
    };

    const hasCompletedProficiency = (certProficiency) => proficiencyCompletions.some((completion) => {
        return completion.certification_proficiency_id === certProficiency.id;
    });

    const isEligibleForFinal = useMemo(() => {
        return proficiencyCompletions.length === certificationProficiencies.length;
    }, [proficiencyCompletions, certificationProficiencies]);

    const finalExamPod = pods.find((pod) => pod.type === 'certification-exam-component');

    if (!currentAccount) {
        return <DottedSpinner/>;
    }

    const handleNavigateToProficiency = (certProficiency) => (event) => {
        try {
            const proficiency = proficiencies.find(prof => prof.id === certProficiency.proficiency_id);
            const proficiencyIdentity = proficiency.identity;
            const pod = pods.find(pod => pod.itself === proficiencyIdentity);
            navigate(`/${pod.identity}`);
        } catch (e) {
            temporaryMessage("Unable to navigate to that. Please refresh and try again.");
        }
    };

    const handleClickStart = () => {
        const requiresPayment = pods.some((pod) => pod.onlyPaid);
        (!requiresPayment || hasPaidPlan) ? createEnrollment() : redirect(Routes.path_path('pricing'));
    };

    return (
        <>
            <h1>Miracle Fundamentals</h1>
            <div className="site-row m-0 pair-row">
                <div className="pair pair-first flex-3">
                    <p>Begin the journey to miracles with Miracle Fundamentals, a course within
                        The Miracle Worker curriculum. It presents a
                        definition-based approach to understanding and
                        practicing miracle working as a living, modern discipline.</p>
                    <p>Miracle Fundamentals places you in a position to develop genuine proficiency as
                        an active miracle worker in modern times. While miracle working is unlike physical skill,
                        it is a learnable discipline — one that reshapes experience from the inside out.</p>
                </div>
                <div className="pair pair-second flex-1">
                    <img className="w-100" src="https://aa-east2-images.s3.us-east-2.amazonaws.com/miracling-mf-square.png" alt="Miracle Fundamentals" />
                </div>
            </div>

            <div className="site-row">
                <hr/>
            </div>

            <div className="site-row pair-row m-0">
                <div className="pair pair-first flex-2">
                    <h2>My Miracle Fundamentals</h2>
                    {currentEnrollmentsLoading && <DottedSpinner />}

                    {!currentEnrollmentsLoading && thisEnrollment &&!proficienciesLoading && (
                        <>
                            <div className="pb-4">
                                {certificationProficiencies.map((certProficiency) => {
                                    const proficiency = proficiencies.find((p) => p.id === certProficiency.proficiency_id);
                                    if (!proficiency) return;
                                    const completed = hasCompletedProficiency(certProficiency);
                                    return (
                                        <Button
                                            className={`proficiency-card ${completed ? 'completed' : ''}`}
                                            key={certProficiency.id}
                                            onClick={handleNavigateToProficiency(certProficiency)}
                                        >
                                            <div className="checkbox"></div>
                                            <div className="proficiency-name">{proficiency.name}</div>
                                        </Button>
                                    );
                                })}
                            </div>

                            <div className="site-row"><hr/></div>

                            {!!finalExamPod && (
                                <div className="site-row">
                                    <h2>Certification Exam</h2>
                                    <p>
                                        Successful completion of the course results in Certification in Miracle
                                        Fundamentals. Certification signifies:
                                    </p>
                                    <ul>
                                        <li>Mastery of the foundational Miracle Principles</li>
                                        <li>Demonstrated ability to apply miracle working personally</li>
                                        <li>Readiness to advance within The Miracle Worker curriculum</li>
                                    </ul>

                                    {!!passingResult ? (
                                        <Link to={`/${finalExamPod.identity}`} className="site-btn btn btn-complete">
                                            <span className="fa fa-medal"></span> View Results
                                        </Link>
                                    ) : (
                                        isEligibleForFinal ? (
                                            <Link to={`/${finalExamPod.identity}`} className="site-btn">Start the Final Exam</Link>
                                        ) : (
                                            <Button disabled className='site-btn'>Not Yet Eligible</Button>
                                        )
                                    ) }


                                </div>
                            )}
                        </>
                    )}

                    {!currentEnrollmentsLoading && !thisEnrollment && (
                        <>
                            <p className="mb-4">Start today and begin your way through the curriculum. Proficiencies include:</p>
                            <ul className="mb-4">
                                {Object.values(proficiencies).map((proficiency) => (
                                    <li key={proficiency.id}>{proficiency.name}</li>
                                ))}
                            </ul>
                            <Button onClick={handleClickStart} className="btn site-btn">Start This Certification</Button>
                        </>
                    )}
                </div>
                <div className="pair pair-second flex-1">
                    <h3>Welcome Booklet</h3>
                    {pods.filter((pod) => pod.type === 'pdf').map(pod => {
                        const canAccessPod = canAccessFeature(featurePlan, pod);
                        return canAccessPod ? (
                            <p key={pod.identity}>
                                <Link
                                    to={`/${pod.identity}`}
                                >{pod.navTitle}</Link>
                                &nbsp;&nbsp;<PDFSVG width={12}/>
                            </p>
                        ) : (
                            <p key={pod.identity} className="muted">
                                {pod.navTitle}&nbsp;&nbsp;<PDFSVG width={12}/>
                            </p>
                        )
                    })}
                </div>
            </div>
        </>
    );
};

export default MiracleFundamentalsLanding;